import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

export function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function createNaturalArithmeticTask({ type, pickData, explain }) {
  function generateTask() {
    return {
      type,
      data: pickData(),
      tags: ["liczby_naturalne", "dodawanie_odejmowanie"],
    };
  }

  function renderTask({ data }) {
    return (
      <div style={{ textAlign: "left" }}>
        <p>
          Oblicz: <InlineMath math={`${data.a} ${data.op} ${data.b}`} />.
        </p>
      </div>
    );
  }

  function renderInput(_task, userAnswer, onChange, disabled) {
    return (
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="wynik"
        placeholder="np. 12"
      />
    );
  }

  function checkAnswer({ data }, userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object") return false;
    const parsed = userAnswer.parsed;
    return Number.isFinite(parsed) && parsed === data.result;
  }

  function renderAnswer({ data }) {
    return <InlineMath math={`${data.result}`} />;
  }

  function renderUserAnswer(userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
      return <i>(brak odpowiedzi)</i>;
    }
    return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
  }

  function renderExplanation({ data }) {
    return explain(data);
  }

  return {
    generateTask,
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation,
  };
}

export function simpleAdditionExplanation(data) {
  const aTens = Math.floor(data.a / 10) * 10;
  const aOnes = data.a % 10;
  const bTens = Math.floor(data.b / 10) * 10;
  const bOnes = data.b % 10;

  if (data.a < 10 && data.b < 10) {
    return (
      <div style={{ textAlign: "left" }}>
        <p>Dodajemy liczby bez przekraczania dziesiątki.</p>
        <p>
          <InlineMath math={`${data.a}+${data.b}=${data.result}`} />
        </p>
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left" }}>
      <p>Rozbijamy liczby na dziesiątki i jedności.</p>
      <p>
        <InlineMath math={`${data.a}=${aTens}+${aOnes}`} />,{" "}
        <InlineMath math={`${data.b}=${bTens}+${bOnes}`} />.
      </p>
      <p>
        Dziesiątki: <InlineMath math={`${aTens}+${bTens}=${aTens + bTens}`} />.
      </p>
      <p>
        Jedności: <InlineMath math={`${aOnes}+${bOnes}=${aOnes + bOnes}`} />.
      </p>
      <p>
        <strong>Wynik:</strong>{" "}
        <InlineMath math={`${aTens + bTens}+${aOnes + bOnes}=${data.result}`} />.
      </p>
    </div>
  );
}

export function carryAdditionExplanation(data) {
  const toTen = 10 - (data.a % 10 || 10);
  const rest = data.b - toTen;
  const first = data.a + toTen;

  if (data.a < 10 && data.b < 10) {
    return (
      <div style={{ textAlign: "left" }}>
        <p>Dopełniamy pierwszą liczbę do dziesiątki.</p>
        <p>
          Brakuje <InlineMath math={`${toTen}`} /> do 10, więc rozbijamy{" "}
          <InlineMath math={`${data.b}=${toTen}+${rest}`} />.
        </p>
        <p>
          <InlineMath math={`${data.a}+${toTen}=10`} />, a potem{" "}
          <InlineMath math={`10+${rest}=${data.result}`} />.
        </p>
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left" }}>
      <p>Najpierw dopełniamy jedności do pełnej dziesiątki.</p>
      <p>
        Do <InlineMath math={`${data.a}`} /> brakuje{" "}
        <InlineMath math={`${toTen}`} /> do <InlineMath math={`${first}`} />.
      </p>
      <p>
        Rozbijamy <InlineMath math={`${data.b}=${toTen}+${rest}`} />.
      </p>
      <p>
        <InlineMath math={`${data.a}+${toTen}=${first}`} />, a potem{" "}
        <InlineMath math={`${first}+${rest}=${data.result}`} />.
      </p>
    </div>
  );
}

export function simpleSubtractionExplanation(data) {
  const aTens = Math.floor(data.a / 10) * 10;
  const aOnes = data.a % 10;
  const bTens = Math.floor(data.b / 10) * 10;
  const bOnes = data.b % 10;

  if (data.a < 10 && data.b < 10) {
    return (
      <div style={{ textAlign: "left" }}>
        <p>Odejmujemy bez przekraczania dziesiątki.</p>
        <p>
          <InlineMath math={`${data.a}-${data.b}=${data.result}`} />
        </p>
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left" }}>
      <p>Odejmujemy osobno dziesiątki i jedności.</p>
      <p>
        Dziesiątki: <InlineMath math={`${aTens}-${bTens}=${aTens - bTens}`} />.
      </p>
      <p>
        Jedności: <InlineMath math={`${aOnes}-${bOnes}=${aOnes - bOnes}`} />.
      </p>
      <p>
        <strong>Wynik:</strong>{" "}
        <InlineMath math={`${aTens - bTens}+${aOnes - bOnes}=${data.result}`} />.
      </p>
    </div>
  );
}

export function borrowSubtractionExplanation(data) {
  if (data.b >= 10) {
    const bTens = Math.floor(data.b / 10) * 10;
    const bOnes = data.b % 10;
    const afterTens = data.a - bTens;
    const toLowerTen = afterTens % 10;
    const rest = bOnes - toLowerTen;
    const lowerTen = afterTens - toLowerTen;

    return (
      <div style={{ textAlign: "left" }}>
        <p>Najpierw odejmujemy dziesiątki, a potem jedności rozbijamy do pełnej dziesiątki.</p>
        <p>
          Rozbijamy <InlineMath math={`${data.b}=${bTens}+${bOnes}`} />.
        </p>
        <p>
          Najpierw: <InlineMath math={`${data.a}-${bTens}=${afterTens}`} />.
        </p>
        <p>
          Teraz odejmujemy <InlineMath math={`${bOnes}`} />: najpierw{" "}
          <InlineMath math={`${toLowerTen}`} /> do pełnej dziesiątki, potem{" "}
          <InlineMath math={`${rest}`} />.
        </p>
        <p>
          <InlineMath math={`${afterTens}-${toLowerTen}=${lowerTen}`} />, a potem{" "}
          <InlineMath math={`${lowerTen}-${rest}=${data.result}`} />.
        </p>
      </div>
    );
  }

  const ones = data.a % 10;
  const toLowerTen = ones === 0 ? 10 : ones;
  const rest = data.b - toLowerTen;
  const first = data.a - toLowerTen;

  return (
    <div style={{ textAlign: "left" }}>
      <p>Najpierw schodzimy do pełnej dziesiątki, a potem odejmujemy resztę.</p>
      <p>
        Rozbijamy <InlineMath math={`${data.b}=${toLowerTen}+${rest}`} />.
      </p>
      <p>
        <InlineMath math={`${data.a}-${toLowerTen}=${first}`} />, a potem{" "}
        <InlineMath math={`${first}-${rest}=${data.result}`} />.
      </p>
    </div>
  );
}
